import { useEffect, useRef } from 'react';
import { useSubscriptionStore } from '../stores/subscription.store';
import { useMetricsStore } from '../stores/metrics.store';
import { useUser } from '@/features/auth/model/auth.selectors';
import { wsLogger } from '@/shared/lib/logger';
import type { DataChangedEvent } from './use-realtime-sync';
import { registerDefaultHandlers } from '../realtime-sync.handlers';
import type { getBroadcastCoordinator } from '../broadcast-coordinator';

const DATA_CHANGED_EVENT = 'data_changed';

interface UseWebSocketListenersProps {
  isConnected: boolean;
  isLeader: boolean;
  handleDataChanged: (event: DataChangedEvent) => void;
  unwrapEventData: (data: unknown) => DataChangedEvent | null;
  coordinatorRef: React.RefObject<ReturnType<
    typeof getBroadcastCoordinator
  > | null>;
}

export function useWebSocketListeners({
  isConnected,
  isLeader,
  handleDataChanged,
  unwrapEventData,
  coordinatorRef,
}: UseWebSocketListenersProps) {
  const listenersRef = useRef<Map<string, string>>(new Map());
  const handlersRegisteredRef = useRef(false);

  const user = useUser();

  const listen = useSubscriptionStore((state) => state.listen);
  const stopListening = useSubscriptionStore((state) => state.stopListening);

  const addGlobalHandler = useMetricsStore((state) => state.addGlobalHandler);

  // Register default instant handlers once
  useEffect(() => {
    if (handlersRegisteredRef.current) return;
    handlersRegisteredRef.current = true;

    const cleanup = registerDefaultHandlers();
    return () => {
      cleanup();
      handlersRegisteredRef.current = false;
    };
  }, []);

  // Setup event listener for data_changed event
  // Only leader tab sets up actual WebSocket listeners
  useEffect(() => {
    if (!isConnected || !user?.id || !isLeader) {
      return;
    }

    const userChannel = `user.${user.id}`;

    // Check if we already have listener for this user
    if (listenersRef.current.has(`${userChannel}:${DATA_CHANGED_EVENT}`)) {
      const firstKey = listenersRef.current.keys().next().value;
      if (firstKey && firstKey.startsWith(userChannel)) {
        return;
      }
    }

    // Clear existing listeners before setting up new ones
    listenersRef.current.forEach((subscriptionId) => {
      stopListening(subscriptionId);
    });
    listenersRef.current.clear();

    // Listen for unified data_changed event
    const subscriptionId = listen(
      userChannel,
      DATA_CHANGED_EVENT,
      (data: unknown) => {
        wsLogger.debug(
          `Received ${DATA_CHANGED_EVENT} event on ${userChannel}:`,
          data
        );

        const eventData = unwrapEventData(data);
        if (eventData) {
          handleDataChanged(eventData);

          // Broadcast to follower tabs
          coordinatorRef.current?.broadcastWebSocketMessage(
            userChannel,
            DATA_CHANGED_EVENT,
            data
          );
        }
      }
    );

    listenersRef.current.set(
      `${userChannel}:${DATA_CHANGED_EVENT}`,
      subscriptionId
    );

    // Setup global handler for debugging
    const unsubscribeGlobal = addGlobalHandler((message) => {
      wsLogger.debug('Global WebSocket message:', {
        channel: message.channel,
        event: message.event,
        data: message.data,
      });
    });

    wsLogger.info('WebSocket listener setup complete for data_changed event');

    // Capture current listeners for cleanup
    const currentListeners = listenersRef.current;

    return () => {
      currentListeners.forEach((subscriptionId) => {
        stopListening(subscriptionId);
      });
      currentListeners.clear();
      unsubscribeGlobal();
    };
  }, [
    isConnected,
    user?.id,
    isLeader,
    listen,
    stopListening,
    addGlobalHandler,
    handleDataChanged,
    unwrapEventData,
    coordinatorRef,
  ]);
}
